import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { AuthContext } from './AuthContext';
import { tokenService } from '../services/tokenService';
import { getExistingSessionId, clearSessionId } from '@shared/hooks/useSessionId';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthContextValue,
} from '../types/auth.types';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const refreshUser = useCallback(async () => {
    const { authService } = await import('@features/auth/services/authService');
    const userData = await authService.getMe();
    setUser(userData);
  }, []);

  // Restore the session on mount. The access token lives only in memory, so
  // on a fresh page load we always attempt a silent refresh: the httpOnly
  // cookie (if present) is sent automatically. A leftover localStorage token
  // from the pre-cookie build is passed once as a migration bridge.
  useEffect(() => {
    const initializeAuth = async () => {
      // Guests (no session hint, no legacy token) skip the refresh entirely,
      // so the page renders immediately without a doomed round-trip.
      if (!tokenService.shouldAttemptRefresh()) {
        setIsLoading(false);
        return;
      }
      try {
        const { authService } =
          await import('@features/auth/services/authService');
        const legacyRefresh = tokenService.takeLegacyRefreshToken();
        const tokens = await authService.refreshToken(legacyRefresh);
        tokenService.setAccessToken(tokens.accessToken);
        tokenService.setSessionHint();
        await refreshUser();
      } catch {
        // No valid refresh cookie → not logged in. Clear any stale state.
        tokenService.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [refreshUser]);

  const login = useCallback(async (credentials: LoginRequest) => {
    const { authService } = await import('@features/auth/services/authService');

    // Get guest session ID for cart merge (if any)
    const guestSessionId = getExistingSessionId();

    const response = await authService.login({
      ...credentials,
      guestSessionId: guestSessionId || undefined,
    });
    // Access token in memory only; refresh token is set as an httpOnly cookie
    // by the backend and never touched by JS. The hint lets the next page load
    // know to attempt a silent refresh.
    tokenService.setAccessToken(response.accessToken);
    tokenService.setSessionHint();

    // Clear guest session ID after successful login (cart is now merged)
    if (guestSessionId) {
      clearSessionId();
    }

    const userData = await authService.getMe();
    setUser(userData);
  }, []);

  const register = useCallback(async (data: RegisterRequest) => {
    const { authService } = await import('@features/auth/services/authService');
    await authService.register(data);
  }, []);

  const logout = useCallback(async () => {
    try {
      const { authService } =
        await import('@features/auth/services/authService');
      // Backend reads the refresh cookie, revokes it and clears it.
      await authService.logout();
    } catch {
      // Ignore logout errors - still clear local state
    } finally {
      tokenService.clearTokens();
      setUser(null);
    }
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      const { authService } =
        await import('@features/auth/services/authService');
      await authService.logoutAll();
    } catch {
      // Ignore logout errors - still clear local state
    } finally {
      tokenService.clearTokens();
      setUser(null);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    logoutAll,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
